import type {
  AutomationAction,
  AutomationActionState,
  AutomationActionType,
  AutomationRunStatus,
  AutomationTrigger,
  AutomationTriggerType,
} from '../contracts/automations';
import type { StatusTone } from '../contracts/common';

/** Automatyzacje (Ustawienia → Automatyzacje; Fala 10). */

export const AUTOMATION_TRIGGER_LABELS: Record<AutomationTriggerType, string> = {
  'incident.opened': 'Awaria strony albo serwera',
  'incident.resolved': 'Koniec awarii',
  'message.urgent': 'Pilny mail od klienta',
  'renewal.due': 'Zbliża się odnowienie',
  'task.overdue': 'Zadanie po terminie',
  'schedule.daily': 'Codziennie o godzinie',
};

export const AUTOMATION_ACTION_LABELS: Record<AutomationActionType, string> = {
  summary: 'Podsumowanie Copilota',
  brief: 'Poranny brief',
  create_task: 'Utwórz zadanie',
  delegate_claude: 'Zleć Claude',
  notify: 'Powiadom mnie',
};

export const AUTOMATION_ACTION_HINTS: Record<AutomationActionType, string> = {
  summary: 'Kontekst w powiadomieniu: od kiedy, ostatnie maile klienta, ostatnie przebiegi agentów.',
  brief: 'Podsumowanie dnia od Copilota: zadania, akceptacje, alarmy.',
  create_task: 'Zadanie z tytułem z szablonu, np. „Pilny mail: {temat}”.',
  delegate_claude: 'Przebieg Claude z instrukcją z szablonu — tylko po zgodzie właściciela, z limitem na dobę.',
  notify: 'Pozycja w Inboxie, push wg Twoich ustawień, opcjonalnie mail na Twój adres.',
};

export const AUTOMATION_RUN_STATUS_LABELS: Record<AutomationRunStatus, string> = {
  queued: 'W kolejce',
  running: 'W toku',
  succeeded: 'Wykonano',
  partial: 'Częściowo',
  failed: 'Błąd',
  skipped: 'Pominięto',
};

export const AUTOMATION_RUN_STATUS_TONES: Record<AutomationRunStatus, StatusTone> = {
  queued: 'neutral',
  running: 'accent',
  succeeded: 'success',
  partial: 'warning',
  failed: 'danger',
  skipped: 'neutral',
};

export const AUTOMATION_ACTION_STATE_LABELS: Record<AutomationActionState, string> = {
  pending: 'czeka',
  started: 'w toku',
  done: 'zrobione',
  skipped: 'pominięte',
  failed: 'błąd',
  uncertain: 'wynik nieznany',
};

function daysText(n: number): string {
  if (n === 0) return 'w dniu terminu';
  return n === 1 ? '1 dzień przed' : `${n} dni przed`;
}

/** Opis wyzwalacza w jednej linii, np. „Zbliża się odnowienie · 14 dni przed”. */
export function automationTriggerText(t: AutomationTrigger, names?: { assets?: number; clients?: number }): string {
  const base = AUTOMATION_TRIGGER_LABELS[t.type];
  switch (t.type) {
    case 'incident.opened':
    case 'incident.resolved': {
      const parts: string[] = [];
      if (t.assetIds.length) parts.push(`${names?.assets ?? t.assetIds.length} zas.`);
      if (t.clientIds.length) parts.push(`${names?.clients ?? t.clientIds.length} klient.`);
      return parts.length ? `${base} · ${parts.join(', ')}` : `${base} · wszystkie zasoby`;
    }
    case 'message.urgent':
      return t.clientIds.length ? `${base} · wybrani klienci` : `${base} · każdy klient`;
    case 'renewal.due':
      return `${base} · ${daysText(t.daysBefore)}`;
    case 'task.overdue':
      return `${base} · ${t.onlyMine ? 'moje zadania' : 'zadania, które widzę'}`;
    case 'schedule.daily':
      return `${t.weekdaysOnly ? 'W dni robocze' : 'Codziennie'} o ${t.time}`;
  }
}

/** Akcje w jednej linii: „Podsumowanie Copilota, Zleć Claude, Powiadom mnie (pilne)”. */
export function automationActionsText(actions: readonly AutomationAction[]): string {
  return actions
    .map((a) => {
      if (a.type === 'notify') {
        const extras = [a.urgent ? 'pilne' : null, a.email ? 'mail' : null].filter(Boolean);
        return extras.length
          ? `${AUTOMATION_ACTION_LABELS.notify} (${extras.join(', ')})`
          : AUTOMATION_ACTION_LABELS.notify;
      }
      return AUTOMATION_ACTION_LABELS[a.type];
    })
    .join(', ');
}
